import { ChartLegendItem, StyledText } from 'UI';
import { FlashcardAnswerStatsDto } from 'api/generated';
import React, { FC, useMemo } from 'react';
import { selectors, useAppSelector } from 'store';
import styled, { css, useTheme } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';

type FlashcardsStatisticsProps = {
  className?: string;
};

enum StatisticNameEnum {
  YES = 'yes',
  KINDA = 'kinda',
  NO = 'no',
}

type Statistics = {
  yes: null | FlashcardAnswerStatsDto;
  kinda: null | FlashcardAnswerStatsDto;
  no: null | FlashcardAnswerStatsDto;
};

const FlashcardsStatistics: FC<FlashcardsStatisticsProps> = ({ className }) => {
  const { colors } = useTheme();
  const isDarkMode = useAppSelector(selectors.settings.isDarkMode);
  const flashcardsStatistics = useAppSelector(selectors.performance.selectFlashcardsStatistics);

  const statistics = useMemo(() => {
    if (flashcardsStatistics) {
      const statistics: Statistics = {
        yes: flashcardsStatistics.yes,
        kinda: flashcardsStatistics.kinda,
        no: flashcardsStatistics.no,
      };

      return statistics;
    }
  }, [flashcardsStatistics]);

  const isOnlyIncorrect =
    statistics?.[StatisticNameEnum.YES]?.percent === 0 && statistics?.[StatisticNameEnum.NO]?.percent === 0;

  return (
    <Container className={className}>
      <Title>Flashcards</Title>
      <Indicator $isDarkMode={isDarkMode}>
        <Correct $percent={statistics?.[StatisticNameEnum.YES]?.percent || 0} />
        <Kinda
          $percent={statistics?.[StatisticNameEnum.KINDA]?.percent || 0}
          $isFirst={statistics?.[StatisticNameEnum.YES]?.percent === 0}
        />
        <Incorrect $percent={statistics?.['no']?.percent || 0} $isFirst={isOnlyIncorrect} />
      </Indicator>
      <LegendContainer>
        <ChartLegendItem
          title="Yes"
          color={colors.system.green}
          percent={Math.round(statistics?.[StatisticNameEnum.YES]?.percent || 0)}
          amount={Math.round(statistics?.[StatisticNameEnum.YES]?.count || 0)}
        />
        <ChartLegendItem
          title="Kinda"
          color={colors.system.blue}
          percent={Math.round(statistics?.[StatisticNameEnum.KINDA]?.percent || 0)}
          amount={Math.round(statistics?.[StatisticNameEnum.KINDA]?.count || 0)}
        />
        <ChartLegendItem
          title="No"
          color={colors.system.red}
          percent={Math.round(statistics?.[StatisticNameEnum.NO]?.percent || 0)}
          amount={Math.round(statistics?.[StatisticNameEnum.NO]?.count || 0)}
        />
      </LegendContainer>
    </Container>
  );
};

export default FlashcardsStatistics;

const Container = styled.div`
  width: 100%;
  background-color: ${({ theme: { colors } }) => colors.neutrals[11]};
  border-radius: 12px;
`;

const Title = styled(StyledText)`
  ${({ theme: { typography } }) => typography.headline_semibold_18};

  ${({ theme: { typography } }) => respondToWidth.s`
    ${typography.body_large_semibold_16};
  `}
`;

const Indicator = styled.div<{ $isDarkMode?: boolean }>`
  ${({ theme: { colors }, $isDarkMode }) => css`
    width: 100%;
    height: 24px;
    margin-block: 16px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: start;
    background-color: ${$isDarkMode ? colors.neutrals[6] : colors.neutrals[4]};

    ${respondToWidth.s`
      margin-block:10px;
    `}
  `};
`;

const Correct = styled.div<{ $percent: number }>`
  background-color: ${({ theme: { colors } }) => colors.neutrals[11]};
  height: 26px;
  border-radius: 10px/17px;
  position: relative;
  z-index: 2;
  ${({ $percent }) =>
    css`
      display: ${!$percent && 'none'};
      width: ${$percent ? `calc(${$percent}% + 6px)` : 0};
      min-width: ${$percent ? '30px' : 0};
    `};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    height: 24px;
    width: calc(100% - 6px);
    border-radius: 13px;
    background-color: ${({ theme: { colors } }) => colors.system.green};
    z-index: 1;
  }
`;

const Kinda = styled.div<{ $percent: number; $isFirst: boolean }>`
  background-color: ${({ theme: { colors } }) => colors.neutrals[11]};
  height: 26px;
  border-top-right-radius: 10px 17px;
  border-bottom-right-radius: 10px 17px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-left: none;
  position: relative;
  z-index: 1;

  ${({ $percent, $isFirst }) =>
    css`
      display: ${!$percent && 'none'};
      width: ${$percent ? `calc(${$percent}% ${$isFirst ? '+ 6px' : '- 9px'})` : 0};
      min-width: ${$isFirst ? '30px' : $percent ? '20px' : 0};
      right: ${$isFirst ? 0 : '-6px'};
    `};

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    height: 24px;
    background-color: ${({ theme: { colors } }) => colors.system.blue};
    z-index: 1;
    ${({ $isFirst }) => css`
      width: ${$isFirst ? 'calc(100% - 6px)' : 'calc(100% + 9px)'};
      border-radius: ${$isFirst ? '13px' : '0 13px 13px 0'};
      left: ${$isFirst ? 0 : '-15px'};
    `}
  }
`;

const Incorrect = styled.div<{ $percent: number; $isFirst: boolean }>`
  background-color: ${({ theme: { colors } }) => colors.neutrals[11]};
  height: 26px;
  border-top-right-radius: 10px 17px;
  border-bottom-right-radius: 10px 17px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  position: relative;

  ${({ $percent, $isFirst }) =>
    css`
      display: ${!$percent && 'none'};
      width: ${$percent ? `calc(${$percent}% ${$isFirst ? '+' : '-'} 6px)` : 0};
      min-width: ${$isFirst ? '30px' : $percent ? '26px' : 0};
      right: ${$isFirst ? 0 : '-6px'};
    `};

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    height: 24px;
    background-color: ${({ theme: { colors } }) => colors.system.red};
    ${({ $isFirst }) => css`
      left: ${$isFirst ? 0 : ' -15px'};
      width: ${$isFirst ? 'calc(100% - 6px)' : 'calc(100% + 9px)'};
      border-radius: ${$isFirst ? '13px' : '0 13px 13px 0'};
    `}
  }
`;

const LegendContainer = styled.div`
  width: 100%;
  display: flex;
  height: fit-content;
  justify-content: space-between;
  gap: 10px;
`;
